import styled from "@emotion/styled";
import React, { useState, useEffect } from "react";
import mobileMenuIcon from "@src/assets/icons/mobileMenu.svg";
import MenuIcon from "@src/assets/icons/menu.svg";
import closeIcon from "@src/assets/icons/close.svg";
import { Column, Row } from "@components/Flex";
import MobileMenu from "@components/Header/MobileMenu";
import SizedBox from "@components/SizedBox";
import Wallet from "@components/Wallet/Wallet";
import { observer } from "mobx-react-lite";
import { PRODUCTS, ROUTES } from "@src/constants";
import { useLocation, Link } from "react-router-dom";
import { useTheme } from "@emotion/react";
import Tooltip from "@components/Tooltip";
import LinkGroup from "@components/LinkGroup";
import isRoutesEquals from "@src/utils/isRoutesEquals";
import { ReactComponent as Arrow } from "@src/assets/icons/arrowDownTransparent.svg";
import ProductList from "../ProductList";
import { ReactComponent as MediumIcon } from "@src/assets/links/medium.svg";
import { ReactComponent as GitHubIcon } from "@src/assets/links/github.svg";
import { ReactComponent as XIcon } from "@src/assets/links/x.svg";
import { ReactComponent as TelegramIcon } from "@src/assets/links/telegram.svg";
import { ReactComponent as RobotIcon } from "@src/assets/links/robot.svg";
import { ReactComponent as GuidebookIcon } from "@src/assets/links/guidebook.svg";
import { ReactComponent as RomeLogo } from "@src/assets/tokens/ROME.svg";
import SwapIcon from "@src/assets/links/swap.svg";
import NodeIcon from "@src/assets/links/node.svg";
import LendIcon from "@src/assets/links/lend.svg";
import MarketIcon from "@src/assets/links/market.svg";
import { THEME_TYPE } from "@src/themes/ThemeProvider";
import { useStores } from "@src/stores";
import useWindowSize from "@src/hooks/useWindowSize";
import { ReactComponent as WalletIcon } from "@src/assets/icons/pink-wallet.svg";
import SettingsMenuIcon from "@src/assets/icons/settings.svg";

interface IProps {}

const Root = styled(Column)`
  width: 100%;
  background: ${({ theme }) => theme.colors.white};
  align-items: center;
  z-index: 102;
  box-shadow: 0 8px 56px rgba(54, 56, 112, 0.16);

  a {
    text-decoration: none;
  }
`;

const TopMenu = styled.header`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 64px;
  padding: 0 24px 0 16px;
  max-width: 1440px;
  z-index: 102;
  @media (min-width: 880px) {
    height: 80px;
  }
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.white};

  .logo {
    height: 30px;
    @media (min-width: 880px) {
      height: 36px;
    }
  }

  .icon {
    cursor: pointer;
  }

  @media (min-width: 880px) {
    padding: 0 16px;
  }
`;

const MenuItem = styled.div<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  box-sizing: border-box;
  border-bottom: 4px solid
    ${({ selected, theme }) =>
      selected ? theme.colors.blue500 : "transparent"};
  height: 100%;
  margin: 0 9px;

  a {
    color: ${({ selected, theme }) =>
      selected ? theme.colors.primary800 : theme.colors.primary650};
  }

  &:hover {
    border-bottom: 4px solid ${({ theme }) => theme.colors.primary300};
    a {
      color: ${({ theme }) => theme.colors.blue500};
    }
  }
`;

const Mobile = styled.div`
  display: flex;
  min-width: fit-content;
  @media (min-width: 880px) {
    display: none;
  }
`;

const Desktop = styled.div`
  display: none;
  @media (min-width: 880px) {
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

const MarketsWrapper = styled.div`
  flex: 1 1 auto;
  overflow: hidden;
`;

const Markets = styled.div`
  height: 80px;
  display: flex;
  overflow-x: auto;
  gap: 12px;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;

  padding: 0 8px;

  &::-webkit-scrollbar {
    display: none;
  }

  & > * {
    flex: 0 0 auto;
  }
`;

const RowLinks = styled(Row)`
  margin-top: 15px;
  align-items: center;
  justify-content: center;
  gap: 30px;
  cursor: pointer;
`;

const BurgerMenu = styled.div<{ expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 8px;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.colors.primary100};
  border-radius: 10px;
  cursor: pointer;
  background: ${({ expanded, theme }) =>
    expanded ? theme.colors.primary100 : theme.colors.white};

  :hover {
    background: ${({ theme }) => theme.colors.primary100};
  }
`;

const Header: React.FC<IProps> = () => {
  const [mobileMenuOpened, setMobileMenuOpened] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const location = useLocation();
  const { accountStore } = useStores();
  const theme = useTheme();
  const isDarkTheme = accountStore.selectedTheme === THEME_TYPE.DARK_THEME;
  const { width } = useWindowSize();

  const toggleMenu = (state: boolean) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setMobileMenuOpened(state);
  };

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpened ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [mobileMenuOpened]);

  const menuItems = [
    { name: "Dashboard", link: ROUTES.DASHBOARD },
    { name: "New Market", link: "/3PP9NsYNdmXTqt5bseaegRKHUE76F9MHGgW" },
    { name: "Old Market", link: "/3P4uA5etnZi4AmBabKinq2bMiWU8KcnHZdH" },
    {
      name: "ROME Market",
      icon: <RomeLogo style={{ borderRadius: 500, width: 22, height: 22 }} />,
      link: "/3P8Df2b7ywHtLBHBe8PBVQYd3A5MdEEJAou"
    },
    { name: "Waves DeFi", link: "/3P4DK5VzDwL3vfc5ahUEhtoe5ByZNyacJ3X" },
    { name: "Low Cap", link: "/3PHpuQUPVUoR3AYzFeJzeWJfYLsLTmWssVH" },
    {
      name: "Axly Market",
      link: "https://app.axly.io/lend",
      isExternalLink: true
    }
  ];

  const products = [
    { name: "Puzzle Swap", link: PRODUCTS.SWAP, icon: SwapIcon },
    {
      name: "Puzzle Lend",
      link: PRODUCTS.LEND,
      icon: LendIcon,
      isActive: true
    },
    { name: "Puzzle Market", link: PRODUCTS.MARKET, icon: MarketIcon },
    { name: "Puzzle Node", link: PRODUCTS.NODE, icon: NodeIcon }
  ];

  const communityLinks = [
    {
      icon: <TelegramIcon className={isDarkTheme ? "theme_icon" : ""} />,
      link: "https://t.me/puzzle_network",
      isExternalLink: true
    },
    {
      icon: <XIcon className={isDarkTheme ? "theme_icon" : ""} />,
      link: "https://twitter.com/puzzle_network",
      isExternalLink: true
    },
    {
      icon: <MediumIcon />,
      link: "https://medium.com/@puzzlenetwork",
      isExternalLink: true
    },
    {
      icon: <GitHubIcon />,
      link: "https://github.com/puzzlenetwork",
      isExternalLink: true
    }
  ];

  const communityMenu = [
    {
      icon: (
        <GuidebookIcon
          width={18}
          height={18}
          className={isDarkTheme ? "theme_icon" : ""}
        />
      ),
      name: "Guidebook",
      link: "https://puzzle-lend.gitbook.io/guidebook",
      isExternalLink: true
    },
    {
      icon: (
        <TelegramIcon
          width={18}
          height={18}
          className={isDarkTheme ? "theme_icon" : ""}
        />
      ),
      name: "Puzzle Lend Chat",
      link: "https://t.me/puzzlelend",
      isExternalLink: true
    },
    {
      icon: (
        <RobotIcon
          width={18}
          height={18}
          className={isDarkTheme ? "theme_icon" : ""}
        />
      ),
      name: "Puzzle Lend bot",
      link: "https://t.me/PuzzleLendBot",
      isExternalLink: true
    },
    {
      icon: (
        <RobotIcon
          width={18}
          height={18}
          className={isDarkTheme ? "theme_icon" : ""}
        />
      ),
      name: "Notification bot",
      link: "https://t.me/puzzle_swap",
      isExternalLink: true
    },
    {
      icon: (
        <RobotIcon
          width={18}
          height={18}
          className={isDarkTheme ? "theme_icon" : ""}
        />
      ),
      name: "Alerts bot",
      link: "https://t.me/puzzle_alerts_bot",
      isExternalLink: true
    },
    {
      icon: (
        <GuidebookIcon
          width={18}
          height={18}
          className={isDarkTheme ? "theme_icon" : ""}
        />
      ),
      name: "Terms of Use",
      link: ROUTES.TERMS_OF_SERVICE,
      isExternalLink: false
    }
  ];

  return (
    <Root>
      {width && width < 880 && (
        <Mobile>
          <MobileMenu
            opened={mobileMenuOpened}
            onClose={() => toggleMenu(false)}
          />
        </Mobile>
      )}
      <TopMenu>
        <Row
          alignItems="center"
          crossAxisSize="max"
          style={{ marginRight: 30 }}
        >
          <div style={{ width: 173 }}>
            <Tooltip
              config={{
                placement: "bottom-start",
                trigger: "click",
                onVisibleChange: setIsTooltipOpen
              }}
              content={
                <Column crossAxisSize="max">
                  <ProductList title="" links={products} />
                  <SizedBox
                    height={1}
                    style={{ width: "100%", background: "#F1F2FE" }}
                  />
                  <RowLinks>
                    {communityLinks.map((el) => (
                      <a
                        key={el.link}
                        href={el.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {el.icon}
                      </a>
                    ))}
                  </RowLinks>
                </Column>
              }
            >
              <Row alignItems="center">
                <img
                  className="logo"
                  src={theme.images.icons.logo}
                  alt="logo"
                />
                <Arrow
                  style={{
                    cursor: "pointer",
                    transform: isTooltipOpen ? "rotate(180deg)" : "none",
                    transition: "transform 0.3s ease"
                  }}
                />
              </Row>
            </Tooltip>
          </div>
          <Desktop>
            <MarketsWrapper>
              <Markets>
                {menuItems.map(({ icon, name, link }) => (
                  <MenuItem
                    key={name}
                    selected={isRoutesEquals(link, location.pathname)}
                  >
                    <Link
                      to={link}
                      target={link[0] === "/" ? "_self" : "_blank"}
                    >
                      <Row style={{ gap: 8 }}>
                        {icon}
                        {name}
                      </Row>
                    </Link>
                  </MenuItem>
                ))}
              </Markets>
            </MarketsWrapper>
          </Desktop>
        </Row>
        <Mobile>
          {!mobileMenuOpened && (
            <img
              onClick={() => accountStore.setSettingsSidebarOpened(true)}
              className="icon"
              src={SettingsMenuIcon}
              alt="settings"
              style={{ width: 20, height: 20 }}
            />
          )}
          <SizedBox width={8} />
          {accountStore.address != null && !mobileMenuOpened && (
            <WalletIcon
              onClick={() => accountStore.setWalletModalOpened(true)}
              style={{ cursor: "pointer" }}
            />
          )}
          <SizedBox width={12} />
          <img
            onClick={() => toggleMenu(!mobileMenuOpened)}
            className="icon"
            src={mobileMenuOpened ? closeIcon : mobileMenuIcon}
            alt="menuControl"
          />
        </Mobile>
        <Desktop>
          <Wallet />
          <Tooltip
            config={{
              placement: "bottom-start",
              trigger: "click",
              onVisibleChange: (visible) => {
                if (!visible) {
                  toggleMenu(false);
                }
              }
            }}
            content={
              <Column crossAxisSize="max">
                <LinkGroup title="" links={communityMenu} />
                <SizedBox height={8} />
              </Column>
            }
          >
            <BurgerMenu expanded={false}>
              <img
                onClick={() => toggleMenu(!mobileMenuOpened)}
                className="icon"
                src={MenuIcon}
                alt="menuControl"
              />
            </BurgerMenu>
          </Tooltip>
        </Desktop>
      </TopMenu>
    </Root>
  );
};

export default observer(Header);
